/**
 * Store for analytics data
 * Currently stored only locally, will later be sent to the backend for analysis
 *
 * Draft version, not checked for privacy law compliance
 */

import ApiHandler from '@/logic/ApiHandler';
import ApiRoutes from '@/logic/ApiRoutes';
import { LogError, LogWarning, DeviceInfo, DeviceSettings } from '@/types/Analytics';
import {getField, updateField} from 'vuex-map-fields';

interface SettingChange {
    setting: string;            // Setting that was changed
    oldValue: string;           // Previous setting value
    newValue: string;           // New setting value
    timestamp: number;          // Time the setting was changed in Milliseconds since UNIX epoch
}

interface Click {
    x: number;                  // X-coordinate
    y: number;                  // Y-coordinate
    val: number;                // Click strength, should be 1 for regular click
    timestamp: number;          // Time the click was registered in Milliseconds since UNIX epoch
}

export const analytics = {
    namespaced: true,
    state: {
        deviceID: parseInt(Date.now().toString().slice(4), 10),
        settingChanges: [

        ],
        clicks: [],
        deviceInfo: {},
        heatmap_overlay_global: false,
        show_heatmap_overlay: false,
        errors: [],
        warnings: []
    },
    getters: {
        getField,
        getHeatmapData: (state: any) => {

            const data: any = [];

            state.clicks.forEach((el: Click) => {
                data.push([el.x, el.y, el.val]);
            });
            return data;
        },
        getDeviceInfo: (state: any) => {

            return state.deviceInfo;
        },
        getShowHeatmapOverlay: (state: any) => {
            return state.show_heatmap_overlay;
        },
        getHeatmapOverlayGlobal: (state: any) => {
            return state.heatmap_overlay_global;
        },
        getLogErrors: (state: any) => {
            return state.errors;
        },
        getLogWarnings: (state: any) => {
            return state.warnings;
        },
        getDeviceID: (state: any) => {
            return state.deviceID;
        }
    },
    actions: {
        registerDeviceInfo(context: any, nav: Navigator) {

            const info: DeviceInfo = {
                sessionId: context.state.deviceID,
                timestamp: Date.now(),
                platform: nav.platform,
                userAgent: nav.userAgent,
                languages: nav.languages.toString(),
                screenwidth: window.innerWidth,
                screenheight: window.innerHeight
            };

            context.commit('registerDeviceInfo', info);
        },
        sendHeatmap(context: any) {

            const heatmapdata: object[] = [];
            context.state.clicks.forEach((el: Click) => {
                heatmapdata.push({
                    id: context.state.deviceID,
                    x: el.x,
                    y: el.y,
                    timestamp: el.timestamp
                });
            });

            const request = ApiRoutes.getSendHeatmapRequest(JSON.stringify(heatmapdata));

            // Send data, no further processing needed
            ApiHandler.request(request, true);
        },
        logError(context: any, description: any) {

            const logError: LogError = {
                sessionId: context.state.deviceID,
                timestamp: Date.now(),
                message: description.message,
                details: description.details
            };

            context.commit('logError', logError);
        },
        logWarning(context: any, description: any) {

            const logWarning: LogWarning = {
                sessionId: context.state.deviceID,
                timestamp: Date.now(),
                message: description.message,
                details: description.details
            };

            context.commit('logWarning', logWarning);
        },
        sendLogInfo(context: any) {

            const logErrors = context.state.errors;
            const logWarnings = context.state.warnings;

            const logInfo = logErrors.concat(logWarnings);

            const request = ApiRoutes.getSendLogInfoRequest(JSON.stringify(logInfo));

            ApiHandler.request(request, true);
        },
        sendDeviceInfo(context: any) {

            const request = ApiRoutes.getSendDeviceInfoRequest(JSON.stringify(context.state.deviceInfo));
            ApiHandler.request(request, true);
        },
        sendDeviceSettings(context: any) {

            const settingsState = context.rootState.settings;

            const devSettings: DeviceSettings = {
                sessionId: context.state.deviceID,
                timestamp: Date.now(),
                performance: settingsState.performance,
                profile: settingsState.profile,
                host: settingsState.host.id,
                dark_mode: settingsState.dark_mode,
                show_timestamps: settingsState.show_timestamps,
                show_media: settingsState.show_media,
                order_top_first: settingsState.order_top_first,
                performance_display_type: settingsState.performance_display_type
            };

            const request = ApiRoutes.getSendDeviceSettingsRequest(JSON.stringify(devSettings));
            ApiHandler.request(request, true);
        }
    },
    mutations: {
        registerSettingChange(state: any, data: any) {

            const object: SettingChange = {
                setting: data.setting,
                oldValue: data.oldValue,
                newValue: data.newValue,
                timestamp: Date.now()
            };

            state.settingChanges.push(object);
        },
        registerClick(state: any, clickEvent: MouseEvent) {

            const object: Click = {
                x: clickEvent.clientX,
                y: clickEvent.clientY,
                val: 1,
                timestamp: Date.now()
            };

            state.clicks.push(object);
        },
        registerDeviceInfo(state: any, info: any) {
            state.deviceInfo = info;
        },
        toggleShowHeatmapOverlay(state: any) {
            state.show_heatmap_overlay = !state.show_heatmap_overlay;
        },
        toggleHeatmapOverlayGlobal(state: any) {
            state.heatmap_overlay_global = !state.heatmap_overlay_global;
        },
        logError(state: any, logError: LogError) {
            state.errors.push(logError);
        },
        logWarning(state: any, logWarning: LogWarning) {
            state.warnings.push(logWarning);
        }
    }
};

export const collectAnalyticsAtLoad = (store: any) => {
    store.dispatch('analytics/registerDeviceInfo', navigator);
};
